import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@kit/supabase/database';

/**
 * BAL-900 — the server-side age gate on `professional_profiles.is_profile_public`.
 *
 * The rule lives in the database (migration 20261103010000_profile_publish_age_gate):
 * a BEFORE trigger refuses a transition to public when the owner is under 16
 * (Europe/Zurich calendar date) without a guardian approval, or has no date of
 * birth. This module is the TypeScript side of that contract:
 *
 *   - `getProfilePublishStatus` reads `get_profile_publish_status` so a page can
 *     explain WHY publishing is unavailable before the dancer tries.
 *   - `parsePublishBlockedError` turns the trigger's raw error into a stable
 *     reason, so every writer can show a translated message instead of a raw
 *     Postgres string or a silent "Saved".
 *
 * Nothing here decides anything: the trigger is the authorization boundary.
 */

export const PUBLISH_BLOCK_REASONS = [
  'date_of_birth_missing',
  'guardian_approval_required',
] as const;

export type PublishBlockReason = (typeof PUBLISH_BLOCK_REASONS)[number];

/** Prefix of the trigger's error message: `PROFILE_PUBLISH_BLOCKED:<reason>`. */
export const PUBLISH_BLOCKED_ERROR_PREFIX = 'PROFILE_PUBLISH_BLOCKED:';

export interface ProfilePublishStatus {
  /** NULL when the profile may be public right now. */
  blockReason: PublishBlockReason | null;
  /** Owner is under 16 or has no DOB: publishing depends on a guardian. */
  requiresGuardian: boolean;
  /** Managers (guardians) who have approved publishing. */
  approvalCount: number;
  /** Managers linked to the profile, excluding the owner. */
  managerCount: number;
  /** The caller is a manager of this profile. */
  callerIsManager: boolean;
  /** When the caller approved, if they did. ISO timestamp. */
  callerApprovedAt: string | null;
}

function toBlockReason(value: unknown): PublishBlockReason | null {
  return typeof value === 'string' &&
    (PUBLISH_BLOCK_REASONS as readonly string[]).includes(value)
    ? (value as PublishBlockReason)
    : null;
}

/**
 * Extract the block reason from any error text that carries the trigger's
 * message (a PostgREST error, the RPC's `error` field, a wrapped "Profile
 * update failed: …" string). Returns null for any other error.
 */
export function parsePublishBlockedError(
  message: string | null | undefined,
): PublishBlockReason | null {
  if (!message) return null;
  const index = message.indexOf(PUBLISH_BLOCKED_ERROR_PREFIX);
  if (index === -1) return null;
  const rest = message.slice(index + PUBLISH_BLOCKED_ERROR_PREFIX.length);
  const match = /^[a-z_]+/.exec(rest);
  return toBlockReason(match?.[0]);
}

/**
 * Read the publish status of `profileId` (= the owner's `profiles.id`). The RPC
 * answers only for the owner, a manager, or a super admin. Returns null when the
 * read fails or is refused; callers must then fail closed (treat as blocked or
 * fall back to the age-based affordance), never open.
 */
export async function getProfilePublishStatus(
  client: SupabaseClient<Database>,
  profileId: string,
): Promise<ProfilePublishStatus | null> {
  const { data, error } = await client.rpc('get_profile_publish_status', {
    p_profile_id: profileId,
  });

  if (error || !data || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }

  const row = data as Record<string, unknown>;

  return {
    blockReason: toBlockReason(row.block_reason),
    requiresGuardian: row.requires_guardian === true,
    approvalCount: Number(row.approval_count ?? 0),
    managerCount: Number(row.manager_count ?? 0),
    callerIsManager: row.caller_is_manager === true,
    callerApprovedAt:
      typeof row.caller_approved_at === 'string'
        ? row.caller_approved_at
        : null,
  };
}
